import { DOCUMENT } from '@angular/common';
import { inject, Injectable, NgZone } from '@angular/core';
import { EVENT_MANAGER_PLUGINS, EventManager } from '@angular/platform-browser';
import { type Observable } from 'rxjs';

import { DomService } from '../services/dom.service';

@Injectable({ providedIn: 'root' })
export class CustomEventManager extends EventManager {
  private _domService = inject(DomService, { optional: true });
  private _document = inject<any>(DOCUMENT);

  constructor() {
    const eventManagerPlugins = inject(EVENT_MANAGER_PLUGINS);
    const ngZone = inject(NgZone);

    super(eventManagerPlugins, ngZone);
  }

  public override addEventListener(
    element: HTMLElement,
    eventName: string,
    handler: () => any,
  ): () => any {
    return this._handleZone(
      element,
      eventName,
      actualEventName =>
        super.addEventListener(element, actualEventName, handler) as () => any,
      handler,
    );
  }

  private _handleZone(
    target: string | HTMLElement,
    eventName: string,
    cb: (actualEventName: string) => () => any,
    handler: () => any,
  ): () => any {
    const actualEventName = eventName.slice(0, -9);

    if (eventName.endsWith('.out-zone')) {
      if (target === this._document) {
        target = 'document';
      }

      if (this._domService && typeof target === 'string') {
        switch (`${target}:${actualEventName}`) {
          case 'document:click': {
            return this._fromDomService(this._domService.docClicked(), handler);
          }
          case 'document:keydown.Tab': {
            return this._fromDomService(this._domService.tab(), handler);
          }
          case 'window:resize': {
            return this._fromDomService(this._domService.resized(), handler);
          }
          case 'window:scroll': {
            return this._fromDomService(this._domService.scrolled(), handler);
          }
          case 'window:mousemove': {
            return this._fromDomService(this._domService.mouseMoved(), handler);
          }
        }
      }

      return this.getZone().runOutsideAngular(() => cb(actualEventName));
    }

    return cb(eventName);
  }

  private _fromDomService<T>(
    observable: Observable<T>,
    handler: (event: T) => any,
  ): () => any {
    const o = observable.subscribe(e => handler(e));

    return () => o.unsubscribe();
  }
}

export const EVENT_MANAGER_PROVIDER = {
  provide: EventManager,
  useClass: CustomEventManager,
};
